/**
 * `EX-RTW05`: a problem whose program reads its input from a file and writes
 * its answer to one (`input.txt`, `output.txt`), as old Codeforces rounds,
 * USACO and Polygon's input-file/output-file do. Absent is stdin and stdout.
 */
export type ProblemFileIo = { inputFile?: string; outputFile?: string };

/** The server's rule (`PROBLEM_IO_FILE_PATTERN`), so the editor can say no before a save does. */
export const FILE_IO_NAME_PATTERN = /^[a-z0-9][a-z0-9_-]{0,31}\.(txt|in|out|dat|ans)$/i;

/** True when the problem names at least one file. */
export function hasFileIo(fileIo: ProblemFileIo | null | undefined): fileIo is ProblemFileIo {
  return Boolean(fileIo && (fileIo.inputFile || fileIo.outputFile));
}

/** The `fileIo` a statement takes, present only when it names a file. */
export function fileIoProp(fileIo: ProblemFileIo | null | undefined): { fileIo?: ProblemFileIo } {
  return hasFileIo(fileIo) ? { fileIo } : {};
}

/** Why a name would be refused, or `null` when it is fine (an empty name is stdin or stdout). */
export function fileIoNameProblem(name: string): "pattern" | null {
  const trimmed = name.trim();
  return trimmed === "" || FILE_IO_NAME_PATTERN.test(trimmed) ? null : "pattern";
}

/** The two C++ lines that point stdin and stdout at the problem's files. */
export function cppFileIoSnippet(fileIo: ProblemFileIo): string {
  return [
    fileIo.inputFile ? `freopen("${fileIo.inputFile}", "r", stdin);` : null,
    fileIo.outputFile ? `freopen("${fileIo.outputFile}", "w", stdout);` : null,
  ]
    .filter(Boolean)
    .join("\n");
}
